import { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/notifications';

const STATUS_COLORS = {
  pending:        '#f39c12',
  sent:           '#27ae60',
  link_generated: '#2980b9',
  failed:         '#e74c3c',
  read:           '#95a5a6',
};

const CHANNEL_LABELS = { sms: 'SMS', email: 'Email', whatsapp: 'WhatsApp', in_app: 'In-App' };

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings]           = useState([]);
  const [view, setView]                   = useState('log'); // 'log' | 'settings'
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [editingSetting, setEditingSetting] = useState(null);
  const [editData, setEditData]           = useState({});
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchNotifications(); }, [filterStatus, filterChannel]);
  useEffect(() => { if (view === 'settings') fetchSettings(); }, [view]);

  const fetchNotifications = () => {
    const params = {};
    if (filterStatus)  params.status  = filterStatus;
    if (filterChannel) params.channel = filterChannel;
    axios.get(API, { headers, params }).then(r => setNotifications(r.data)).catch(() => {});
  };

  const fetchSettings = () => {
    axios.get(`${API}/settings`, { headers }).then(r => setSettings(r.data)).catch(() => {});
  };

  const showMsg = (text, type = 'success') => {
    if (type === 'error') setError(text); else setSuccess(text);
    setTimeout(() => { setError(''); setSuccess(''); }, 5000);
  };

  const resend = async (n) => {
    try {
      const res = await axios.post(`${API}/${n.id}/resend`, {}, { headers });
      showMsg('Notification re-dispatched');
      // If WhatsApp, open the link immediately for the user to send
      if (res.data.channel === 'whatsapp' && res.data.wa_link) {
        window.open(res.data.wa_link, '_blank');
      }
      fetchNotifications();
    } catch (err) { showMsg(err.response?.data?.error || 'Resend failed', 'error'); }
  };

  const openWhatsApp = (n) => {
    if (n.wa_link) window.open(n.wa_link, '_blank');
  };

  const markRead = async (id) => {
    try { await axios.patch(`${API}/${id}/mark-read`, {}, { headers }); fetchNotifications(); }
    catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const saveSetting = async (eventType) => {
    try {
      await axios.patch(`${API}/settings/${eventType}`, editData, { headers });
      showMsg('Setting updated'); setEditingSetting(null); fetchSettings();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', 'error'); }
  };

  const inp   = { padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' };
  const btn   = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });
  const smBtn = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' });
  const th    = { padding: '10px', textAlign: 'left', fontSize: '12px' };
  const td    = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };

  const statusBadge = (s) => (
    <span style={{ background: STATUS_COLORS[s] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
      {s?.replace(/_/g, ' ').toUpperCase()}
    </span>
  );

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>Notifications</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setView('log')} style={btn(view === 'log' ? '#2c3e50' : '#95a5a6')}>Log</button>
          <button onClick={() => setView('settings')} style={btn(view === 'settings' ? '#2c3e50' : '#95a5a6')}>Settings</button>
        </div>
      </div>

      {error   && <div style={{ background: '#e74c3c', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '12px' }}>{error}</div>}
      {success && <div style={{ background: '#2ecc71', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '12px' }}>{success}</div>}

      {view === 'log' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'flex-end', background: '#f9f9f9', padding: '12px', borderRadius: '8px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Status</label>
              <select style={inp} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value=''>All</option>
                {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d', marginBottom: '4px' }}>Channel</label>
              <select style={inp} value={filterChannel} onChange={e => setFilterChannel(e.target.value)}>
                <option value=''>All</option>
                {Object.entries(CHANNEL_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
                <th style={th}>Member</th><th style={th}>Event</th><th style={th}>Channel</th>
                <th style={th}>Message</th><th style={th}>Status</th><th style={th}>Attempts</th>
                <th style={th}>Date</th><th style={th}>Actions</th>
              </tr></thead>
              <tbody>
                {notifications.length === 0 && <tr><td colSpan="8" style={{ ...td, textAlign: 'center', color: '#95a5a6' }}>No notifications yet</td></tr>}
                {notifications.map((n, i) => (
                  <tr key={n.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={td}><strong>{n.member_name || '—'}</strong>{n.member_number && <div style={{ fontSize: '11px', color: '#95a5a6' }}>{n.member_number}</div>}</td>
                    <td style={td}>{n.event_type?.replace(/_/g, ' ')}</td>
                    <td style={td}>{CHANNEL_LABELS[n.channel] || n.channel}</td>
                    <td style={{ ...td, maxWidth: '280px' }}>
                      <div style={{ fontWeight: '600' }}>{n.title}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{n.message}</div>
                      {n.error_message && <div style={{ fontSize: '11px', color: '#e74c3c', marginTop: '2px' }}>⚠ {n.error_message}</div>}
                    </td>
                    <td style={td}>{statusBadge(n.status)}</td>
                    <td style={td}>{n.attempt_count}</td>
                    <td style={td}>{n.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {n.channel === 'whatsapp' && n.wa_link && (
                          <button onClick={() => openWhatsApp(n)} style={smBtn('#25D366')}>Open WhatsApp</button>
                        )}
                        <button onClick={() => resend(n)} style={smBtn('#2980b9')}>Resend</button>
                        {n.channel === 'in_app' && n.status !== 'read' && (
                          <button onClick={() => markRead(n.id)} style={smBtn('#95a5a6')}>Mark Read</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === 'settings' && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#1a5276' }}>
            Configure which channel each event type uses, and edit the message template. Use <code>{'{placeholder}'}</code> syntax — placeholders are filled in by the module that triggers the event.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}>
              <th style={th}>Event Type</th><th style={th}>Channel</th><th style={th}>Enabled</th>
              <th style={th}>Title Template</th><th style={th}>Message Template</th><th style={th}>Actions</th>
            </tr></thead>
            <tbody>
              {settings.map((s, i) => (
                editingSetting === s.event_type ? (
                  <tr key={s.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                    <td style={td}><strong>{s.event_type.replace(/_/g, ' ')}</strong></td>
                    <td style={td}>
                      <select value={editData.channel} onChange={e => setEditData({ ...editData, channel: e.target.value })} style={inp}>
                        {Object.entries(CHANNEL_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <select value={editData.enabled} onChange={e => setEditData({ ...editData, enabled: e.target.value === 'true' })} style={inp}>
                        <option value="true">Yes</option><option value="false">No</option>
                      </select>
                    </td>
                    <td style={td}><input value={editData.title_template} onChange={e => setEditData({ ...editData, title_template: e.target.value })} style={{ ...inp, width: '160px' }} /></td>
                    <td style={td}><textarea value={editData.message_template} onChange={e => setEditData({ ...editData, message_template: e.target.value })} style={{ ...inp, width: '280px', height: '60px' }} /></td>
                    <td style={td}>
                      <button onClick={() => saveSetting(s.event_type)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>
                      <button onClick={() => setEditingSetting(null)} style={smBtn('#95a5a6')}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={td}><strong>{s.event_type.replace(/_/g, ' ')}</strong></td>
                    <td style={td}>{CHANNEL_LABELS[s.channel] || s.channel}</td>
                    <td style={td}>{s.enabled ? '✓ Yes' : '✗ No'}</td>
                    <td style={td}>{s.title_template}</td>
                    <td style={{ ...td, maxWidth: '320px', fontSize: '12px', color: '#666' }}>{s.message_template}</td>
                    <td style={td}>
                      <button onClick={() => { setEditingSetting(s.event_type); setEditData({ channel: s.channel, enabled: s.enabled, title_template: s.title_template, message_template: s.message_template }); }} style={smBtn('#e67e22')}>Edit</button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Notifications;